import Vue from 'vue'
import api from '@/apis/siteBookmark'
import {
  PROCESS_API_SUCCESS,
  PROCESS_API_FAILURE,
  CREATE_BOOKMARK_REQUEST,
  CREATE_BOOKMARK_SUCCESS,
  CREATE_BOOKMARK_FAILURE,
  GET_BOOKMARKS_REQUEST,
  GET_BOOKMARKS_SUCCESS,
  GET_BOOKMARKS_FAILURE,
  GET_SITE_BOOKMARKS_REQUEST,
  GET_SITE_BOOKMARKS_SUCCESS,
  GET_SITE_BOOKMARKS_FAILURE,
  DELETE_BOOKMARK_REQUEST,
  DELETE_BOOKMARK_SUCCESS,
  DELETE_BOOKMARK_FAILURE,
} from '@/store/mutation-types'

let initialState = {
  bookmarks: [],
  bookmarkCursor: null,
  hasMoreBookmarks: true,
  siteBookmarks: [],
  siteBookmarkCursor: null,
  hasMoreSiteBookmarks: true,
  status: {
    createBookmark: null,
    getBookmarks: null,
    getSiteBookmarks: null,
    deleteBookmark: null,
  },
}

// initial state
const state = Vue.util.extend({}, initialState)

// getters
const getters = {
  bookmarks: function (state) {
    return state.bookmarks
  },
  bookmarkCursor: function (state) {
    return state.bookmarkCursor
  },
  hasMoreBookmarks: function (state) {
    return state.hasMoreBookmarks
  },
  siteBookmarks: function (state) {
    return state.siteBookmarks
  },
  siteBookmarkCursor: function (state) {
    return state.siteBookmarkCursor
  },
  hasMoreSiteBookmarks: function (state) {
    return state.hasMoreSiteBookmarks
  },
  statusCreateBookmark: function (state) {
    return state.status.createBookmark
  },
  statusGetBookmarks: function (state) {
    return state.status.getBookmarks
  },
  statusGetSiteBookmarks: function (state) {
    return state.status.getSiteBookmarks
  },
  statusDeleteBookmark: function (state) {
    return state.status.deleteBookmark
  },
}

// actions
const actions = {
  createBookmark: function ({ commit }, { siteId, activityLogId, sourceEventTime, partitionNumber }) {
    commit(CREATE_BOOKMARK_REQUEST)
    return new Promise((resolve, reject) => {
      api.createBookmark({ siteId, activityLogId, sourceEventTime, partitionNumber }).then(
        res => {
          let data = res.body
          commit(CREATE_BOOKMARK_SUCCESS, { data })
          resolve(res)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(CREATE_BOOKMARK_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  getBookmarks: function ({ commit }, { cursor, limit, append = false }) {
    commit(GET_BOOKMARKS_REQUEST)
    return new Promise((resolve, reject) => {
      api.getBookmarks({ cursor, limit }).then(
        res => {
          let data = res.body
          commit(GET_BOOKMARKS_SUCCESS, { data, append })
          resolve(res)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(GET_BOOKMARKS_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  getSiteBookmarks: function ({ commit }, { siteId, cursor, limit, append = false }) {
    commit(GET_SITE_BOOKMARKS_REQUEST)
    return new Promise((resolve, reject) => {
      api.getSiteBookmarks({ siteId, cursor, limit }).then(
        res => {
          let data = res.body
          commit(GET_SITE_BOOKMARKS_SUCCESS, { data, append })
          resolve(res)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(GET_SITE_BOOKMARKS_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  deleteBookmark: function ({ commit }, { siteId, bookmarkId }) {
    commit(DELETE_BOOKMARK_REQUEST)
    return new Promise((resolve, reject) => {
      api.deleteBookmark({ siteId, bookmarkId }).then(
        res => {
          let data = res.body
          commit(DELETE_BOOKMARK_SUCCESS, { data })
          resolve(res)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(DELETE_BOOKMARK_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
}

// mutations
const mutations = {
  [CREATE_BOOKMARK_REQUEST]: function (state) {
    state.status.createBookmark = "requested"
  },
  [CREATE_BOOKMARK_SUCCESS]: function (state) {
    state.status.createBookmark = "successful"
  },
  [CREATE_BOOKMARK_FAILURE]: function (state) {
    state.status.createBookmark = "failed"
  },
  [GET_BOOKMARKS_REQUEST]: function (state) {
    state.status.getBookmarks = "requested"
  },
  [GET_BOOKMARKS_SUCCESS]: function (state, { data, append }) {
    const bookmarks = data.data || []
    const thumbnailInfos = data?.thumbnail_urls?.thumbnail_infos || {}
    const videoaiThumbnailInfos = data?.thumbnail_urls?.videoai_thumbnail_infos || {}

    // 썸네일 정보를 북마크에 병합
    const bookmarksWithThumbnails = bookmarks.map(bookmark => {
      const eventId = bookmark.activity_log?.event_id
      if (eventId) {
        if (thumbnailInfos[eventId]) {
          bookmark.thumbnail_url = thumbnailInfos[eventId].thumbnail_url
        }
        if (videoaiThumbnailInfos[eventId]) {
          bookmark.detection_summary = videoaiThumbnailInfos[eventId].detection_summary
        }
      }
      return bookmark
    })

    // append가 true면 기존 배열에 추가, false면 교체
    if (append) {
      state.bookmarks = [...state.bookmarks, ...bookmarksWithThumbnails]
    } else {
      state.bookmarks = bookmarksWithThumbnails
    }

    // cursor 업데이트
    state.bookmarkCursor = data.cursor || data.next_cursor || null

    // 더 이상 데이터가 없으면 hasMoreBookmarks를 false로
    state.hasMoreBookmarks = bookmarksWithThumbnails.length > 0 && state.bookmarkCursor !== null

    state.status.getBookmarks = "successful"
  },
  [GET_BOOKMARKS_FAILURE]: function (state) {
    state.status.getBookmarks = "failed"
  },
  [GET_SITE_BOOKMARKS_REQUEST]: function (state) {
    state.status.getSiteBookmarks = "requested"
  },
  [GET_SITE_BOOKMARKS_SUCCESS]: function (state, { data, append }) {
    const bookmarks = data.data || []
    const thumbnailInfos = data?.thumbnail_urls?.thumbnail_infos || {}
    const videoaiThumbnailInfos = data?.thumbnail_urls?.videoai_thumbnail_infos || {}

    // 썸네일 정보를 북마크에 병합
    const bookmarksWithThumbnails = bookmarks.map(bookmark => {
      const eventId = bookmark.activity_log?.event_id
      if (eventId) {
        if (thumbnailInfos[eventId]) {
          bookmark.thumbnail_url = thumbnailInfos[eventId].thumbnail_url
        }
        if (videoaiThumbnailInfos[eventId]) {
          bookmark.detection_summary = videoaiThumbnailInfos[eventId].detection_summary
        }
      }
      return bookmark
    })

    // append가 true면 기존 배열에 추가, false면 교체
    if (append) {
      state.siteBookmarks = [...state.siteBookmarks, ...bookmarksWithThumbnails]
    } else {
      state.siteBookmarks = bookmarksWithThumbnails
    }

    // cursor 업데이트
    state.siteBookmarkCursor = data.cursor || data.next_cursor || null

    // 더 이상 데이터가 없으면 hasMoreSiteBookmarks를 false로
    state.hasMoreSiteBookmarks = bookmarksWithThumbnails.length > 0 && state.siteBookmarkCursor !== null

    state.status.getSiteBookmarks = "successful"
  },
  [GET_SITE_BOOKMARKS_FAILURE]: function (state) {
    state.status.getSiteBookmarks = "failed"
  },
  [DELETE_BOOKMARK_REQUEST]: function (state) {
    state.status.deleteBookmark = "requested"
  },
  [DELETE_BOOKMARK_SUCCESS]: function (state) {
    state.status.deleteBookmark = "successful"
  },
  [DELETE_BOOKMARK_FAILURE]: function (state) {
    state.status.deleteBookmark = "failed"
  },
}

export default {
  state,
  getters,
  actions,
  mutations,
}